/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/jsx-no-target-blank */
import React, { useState, useEffect, useReducer } from 'react';
import sitesData from '../data/data';
import { prettyLabel, getFirstLetter } from '../helpers/common';
import GitService from '../services/gitlab';
import ClosedMergeRequests from '../components/atoms/closed-merge-requests';
import OpenMergeRequests from '../components/atoms/open-merge-requests';
import DemoDeployment from '../components/atoms/demo-deployment';
import { useParams } from 'react-router-dom';

const COVERAGE_LIMITS = {
  LOW: 70,
  MEDIUM: 90
}

const initialVersions = {
  theme: '',
  core: '',
  latestCore: ''
};

function versionsReducer(state, action) {
  switch (action.type) {
    case 'SET_THEME_VERSION':
      return { ...state, theme: action.version };
    case 'SET_CORE_VERSION':
      return { ...state, core: action.version };
    case 'SET_LATEST_CORE_VERSION':
      return { ...state, latestCore: action.version };
    default:
      return state;
  }
}

export default function Themeinfo() {
  const [codeCoverage, setCodeCoverage] = useState();
  const [versions, dispatch] = useReducer(versionsReducer, initialVersions);
  const params = useParams();

  const theme = sitesData().themes.find(
    // eslint-disable-next-line eqeqeq
    (theme) => theme.id == params.id
  );

  if (!theme) {
    params.history?.push?.('/');
  }

  const isCoreTheme = theme?.name === 'gatsby';

  const codeCoverageBadge = () => {
    if (codeCoverage >= COVERAGE_LIMITS.MEDIUM) return 'bg-success';
    if (codeCoverage >= COVERAGE_LIMITS.LOW) return 'bg-orange';
    return 'bg-red';
  };

  const tagUrl = (themeName, tagVersion) => {
    return `https://gitlab.com/g2m-gentoo/team-floyd/themes/${themeName}/-/tags/${tagVersion}`;
  };

  useEffect(() => {
    if (!theme) return;

    // code coverage
    GitService.getCoverage(theme.id).then((response) => {
      setCodeCoverage(response);
    });

    // current theme version
    GitService.getTags(theme.id).then((response) => {
      if (response.data.length > 0) {
        dispatch({
          type: 'SET_THEME_VERSION',
          version: response.data[0].name.replace('v', '')
        });
      }
    });

    if (isCoreTheme) {
      return;
    }

    // core theme version used by current theme
    GitService.getThemePackage(theme.id, theme.name).then((response) => {
      const coreDep = response.data.dependencies?.['gatsby-core-theme'];
      if (coreDep) {
        dispatch({
          type: 'SET_CORE_VERSION',
          version: coreDep.replace('^', '')
        });
      }
    });

    // latest available core version
    const coreThemeId = sitesData().themes.find(t => t.name === 'gatsby')?.id;
    if (coreThemeId) {
      GitService.getTags(coreThemeId).then((response) => {
        if (response.data.length > 0) {
          dispatch({
            type: 'SET_LATEST_CORE_VERSION',
            version: response.data[0].name.replace('v', '')
          });
        }
      });
    }
  }, []);

  return (
    <div className="container">
      <div className="row">
        <div className="col-lg-8 d-flex">
          <div className="card w-100">
            <div className="card-body">
              <div className="row">
                <div className="col">
                  <div className="d-flex flex-wrap gap-4">
                    <span className={`avatar avatar-lg bg-${theme.color} text-white`}>
                      {getFirstLetter(theme.name)}
                    </span>
                    <div>
                      <h1>{prettyLabel(theme.name)}</h1>
                      <div className="d-flex flex-wrap gap-1">
                        <a
                          href={tagUrl(
                            `${theme.name}-${isCoreTheme ? 'themes' : 'theme'}`,
                            `v${versions.theme}`
                          )}
                          target="_blank"
                        >
                          <span className="badge bg-white-75">
                            <i className="uil uil-rocket"></i>
                            Version: {versions.theme || "N.A."}
                          </span>
                        </a>
                        {!isCoreTheme && (
                          <a
                            href={tagUrl(
                              "gatsby-themes",
                              `v${versions.core}`
                            )}
                            target="_blank"
                          >
                            <span className={`badge badge-secondary`}>
                              <i className="uil uil-rocket"></i>
                              Core v.: {versions.core || "N.A."}
                            </span>
                          </a>
                        )}

                        <span className={`badge ${codeCoverageBadge()}`}>
                          Tests {codeCoverage}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-12 d-flex flex-x2 flex-gutter-sm">
                  {theme.storybook && (
                    <span className="d-flex align-items-center gap-1">
                      <i className="uil uil-globe"></i>
                      <a target="_blank" href={theme.storybook}>
                        Storybook
                      </a>
                    </span>
                  )}

                  {theme.docs && (
                    <span className="d-flex align-items-center gap-1">
                      <i className="uil uil-document-info"></i>
                      <a target="_blank" href={theme.docs}>
                        Documentation
                      </a>
                    </span>
                  )}

                  {theme.demo && (
                    <span className="d-flex align-items-center gap-1">
                      <i className="uil uil-globe"></i>
                      <a target="_blank" href={theme.demo}>
                        Demo Site
                      </a>
                    </span>
                  )}

                  {theme.repo && (
                    <span className="d-flex align-items-center gap-1">
                      <i className="uil uil-code-branch"></i>
                      <a target="_blank" href={theme.repo} rel="noreferrer">
                        Git
                      </a>
                    </span>
                  )}

                  {theme.release_notes && (
                    <span className="d-flex align-items-center gap-1">
                      <i className="uil uil-notes"></i>
                      <a target="_blank" href={theme.release_notes}>
                        Release Notes
                      </a>
                    </span>
                  )}

                  <span className="d-flex align-items-center">
                    <i className="uil uil-list-ui-alt mr-1"></i>
                    <a
                      target="_blank"
                      href="https://gaminginnovationgroup.atlassian.net/wiki/spaces/WPJ/pages/2224816218/Theme"
                    >
                      Workflow
                    </a>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col d-flex">
          <div className="card flex-grow-1">
            <div className="card-body mb-n3">
              <h4>Notifications</h4>
              {(
                (!isCoreTheme && versions.core && versions.latestCore && versions.core !== versions.latestCore)
                || (codeCoverage !== undefined && codeCoverage < COVERAGE_LIMITS.MEDIUM)) ? (
                  <>
                    {!isCoreTheme && versions.core && versions.latestCore && versions.core !== versions.latestCore && (
                      <div className="callout callout-info">
                        Not using the latest core version. Check{" "}
                        <a href={theme.release_notes} target="_blank">
                          release notes
                        </a> and update.
                      </div>
                    )}
                    {codeCoverage !== undefined && codeCoverage < COVERAGE_LIMITS.MEDIUM && (
                      <div className={`mt-2 callout ${codeCoverage < COVERAGE_LIMITS.LOW ?'callout-danger' : 'callout-warning'}`}>
                        Test Coverage below {codeCoverage < COVERAGE_LIMITS.LOW ? COVERAGE_LIMITS.LOW : COVERAGE_LIMITS.MEDIUM}%.
                      </div>
                    )}
                  </>
              ) : (
                <p>There is no notification.</p>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col">
          <DemoDeployment theme={theme} isCoreTheme={isCoreTheme} />
          <OpenMergeRequests projectId={theme.id} />
          <ClosedMergeRequests projectId={theme.id} />
        </div>
      </div>
    </div>
  );
}
